import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import MosSmartDialog from '../common/MosSmartDialog'
import MosSmartTable from '../common/MosSmartTable'
import NumericKeypad from '../common/NumericKeypad'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import AlertUI from '../../../generic/AlertUI'

const roundQty = (value) => Math.round((Number(value) || 0) * 100) / 100

// S21/S33: SELECCION consumption in two steps for a single reference (RN9, INV7). This dialog is
// the second step — the operator already chose the reference in the components panel (step 1);
// here they pick how much to consume from each of that reference's instances located in the
// machine's Machine Storage (Flujo 1 instances endpoint), then report the consumption in one shot
// with `instanceSelections`. This flow only triggers when the mode persisted for the machine is
// SELECCION (context), so the front no longer sends a `strategy` field (#39/S33) — the server
// already knows the mode. The backend recalculates the effective quantity as the sum of
// `instanceSelections` (it does not trust the top-level `quantity`).
//
// The per-instance quantity uses the same touch control as the components/consume widget's "To Use"
// column: a -/+ stepper plus a tappable value that opens the NumericKeypad (decimals only when the
// instance's reference is fractionable), capped at the instance's available quantity.
//
// `requiredQuantity` (the reference's pending amount) drives a running "selected X of Y" total and
// caps the whole selection at what is still needed: an instance can never take more than the smaller
// of its own availability and the remaining budget, so the operator cannot pick more than required.
//
// S16 (#39 Bloque D): the same instance table is reused for the auto-consume report flow, where
// SELECCION requires picking the load(s)/instance(s) BEFORE confirming the OK report — the
// selection there does not post the manual consumption endpoint, it only needs to hand the
// `instanceSelections` back to the caller so it can attach them to the report body. `mode='select'`
// switches only that final step (`onSelect(instanceSelections, quantity)` instead of `apiPost` +
// `onSuccess()`); everything else (fetch, table, quantity state) is shared, so the manual
// consumption path (`mode='consume'`, default) is untouched.
//
// Story 79 / S32-S33 (DG-B2/DG-B17): when the job's technology can generate the missing material and
// what the warehouse offers does not cover the requirement, the operator gets an explicit "create
// required material" action. It is a mark of intent, not an operation: the front never creates
// inventory — it flags the request and Sun generates and consumes in the same transaction, so
// abandoning the dialog cannot leave orphan stock (S35). `selectableQuantity` is what the machine
// warehouse holds for the reference; it comes from the caller because the instances table is
// paginated, so summing the visible page would misjudge the total.
const InstanceSelectionDialog = ({ open, deviceId, referenceId = 0, referenceCode = '', fabricationOrderId, nodeId, partId = 0, requiredQuantity = 0, selectableQuantity = 0, generateComponents = false, onClose, onSuccess, mode = 'consume', onSelect }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert, dismiss] = AlertUI()
  const [quantityByInstanceId, setQuantityByInstanceId] = useState({})
  const [keypadInfo, setKeypadInfo] = useState({ open: false, id: 0, max: 0, val: 0, fractionable: false })
  const [loading, setLoading] = useState(false)
  const [generateMissing, setGenerateMissing] = useState(false)

  useEffect(() => {
    if (open) {
      setQuantityByInstanceId({})
      setKeypadInfo({ open: false, id: 0, max: 0, val: 0, fractionable: false })
      setLoading(false)
      setGenerateMissing(false)
      dismiss()
    }
  }, [open, dismiss])

  // V9: offered if and only if the technology can generate AND what the warehouse offers falls short
  // of what the report requires (empty list or insufficient sum). With enough stock there is nothing
  // to create, and with the flag off the dialog behaves exactly as before (S58).
  const canCreateRequiredMaterial = generateComponents && selectableQuantity < requiredQuantity

  // Paginated (Spring Page) — same RequestPagination contract as the widget's instances endpoint
  // (Flujo 1). The table runs in `pageable` mode and forwards its own page/size/filters/sort; the
  // Page is returned as-is so the table reads `content` + `totalElements`. Sun binds page/size/
  // allSelected into primitives, so a call without RequestPagination fails the bind before the
  // controller runs. The per-instance quantities live in `quantityByInstanceId` (keyed by id), so
  // they survive paging.
  const fetchInstances = useCallback(async (requestPagination) => {
    if (!deviceId || !referenceId) {
      return { content: [], totalElements: 0 }
    }
    return apiGet('mos/machineStorage/' + deviceId + '/reference/' + referenceId + '/instances', requestPagination)
  }, [deviceId, referenceId])

  const selectedTotal = useMemo(
    () => roundQty(Object.values(quantityByInstanceId).reduce((sum, value) => sum + (Number(value) || 0), 0)),
    [quantityByInstanceId]
  )
  const hasBudget = requiredQuantity > 0

  // The max an instance can take: its own availability and, when a required amount is known, the
  // budget still left once every OTHER instance's chosen quantity is accounted for.
  const maxForInstance = useCallback((row) => {
    const instanceAvailable = row.quantityAvailable ?? 0
    if (!hasBudget) return instanceAvailable
    const otherTotal = selectedTotal - (Number(quantityByInstanceId[row.id]) || 0)
    const remaining = Math.max(0, roundQty(requiredQuantity - otherTotal))
    return Math.min(instanceAvailable, remaining)
  }, [hasBudget, requiredQuantity, selectedTotal, quantityByInstanceId])

  const incQty = useCallback((id, max) => {
    setQuantityByInstanceId((prev) => {
      const next = (Number(prev[id]) || 0) + 1
      return { ...prev, [id]: Math.min(next, max) }
    })
  }, [])

  const decQty = useCallback((id) => {
    setQuantityByInstanceId((prev) => {
      const next = Math.max((Number(prev[id]) || 0) - 1, 0)
      return { ...prev, [id]: next }
    })
  }, [])

  const openKeypad = useCallback((row) => {
    const max = maxForInstance(row)
    if (max <= 0) return
    setKeypadInfo({ open: true, id: row.id, max, val: Number(quantityByInstanceId[row.id]) || 0, fractionable: row.fractionable === true })
  }, [maxForInstance, quantityByInstanceId])

  const handleKeypadAccept = useCallback((newVal) => {
    if (!keypadInfo.id) return
    let finalVal = Math.min(newVal, keypadInfo.max)
    finalVal = keypadInfo.fractionable ? Math.round(finalVal * 100) / 100 : Math.floor(finalVal)
    finalVal = Math.max(finalVal, 0)
    setQuantityByInstanceId((prev) => ({ ...prev, [keypadInfo.id]: finalVal }))
  }, [keypadInfo])

  const columns = useMemo(() => [
    { name: t_('Code'), field: 'code', readOnly: true },
    { name: t_('Batch'), field: 'batch', readOnly: true },
    { name: t_('Available in machine'), field: 'quantityAvailable', readOnly: true },
    {
      name: t_('Quantity to consume'),
      field: 'quantityToConsume',
      readOnly: true,
      search: false,
      sortable: false,
      render: (row) => {
        const max = maxForInstance(row)
        const val = Number(quantityByInstanceId[row.id]) || 0
        const canEdit = max > 0
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              data-testid={'instance-consume-decrease-' + row.id}
              variant='contained'
              color='inherit'
              sx={{ minWidth: 44, width: 44, height: 44, bgcolor: 'background.default' }}
              onClick={() => decQty(row.id)}
              disabled={val <= 0}
            >
              <RemoveCircleOutlineIcon fontSize='large' />
            </Button>
            <Box
              data-testid={'instance-consume-qty-' + row.id}
              sx={{
                bgcolor: 'background.default',
                px: 2,
                py: 1.25,
                borderRadius: 1,
                minWidth: 64,
                textAlign: 'center',
                cursor: canEdit ? 'pointer' : 'default',
                opacity: canEdit || val > 0 ? 1 : 0.38,
                '&:hover': { bgcolor: canEdit ? 'action.hover' : 'background.default' }
              }}
              onClick={() => { if (canEdit) openKeypad(row) }}
            >
              <Typography variant='h6' sx={{ lineHeight: 1 }}>{val}</Typography>
            </Box>
            <Button
              data-testid={'instance-consume-increase-' + row.id}
              variant='contained'
              color='inherit'
              sx={{ minWidth: 44, width: 44, height: 44, bgcolor: 'background.default' }}
              onClick={() => incQty(row.id, max)}
              disabled={val >= max}
            >
              <AddCircleOutlineIcon fontSize='large' />
            </Button>
          </Box>
        )
      }
    }
  ], [t_, quantityByInstanceId, maxForInstance, incQty, decQty, openKeypad])

  const submitSelection = useCallback(async () => {
    const instanceSelections = Object.entries(quantityByInstanceId)
      .map(([rawMaterialId, value]) => ({ rawMaterialId: Number(rawMaterialId), quantity: Number(value) }))
      .filter((item) => item.quantity > 0)
    // S33: with the generation marked an empty selection is legitimate — the warehouse may hold
    // nothing at all and Sun accepts quantity 0 alongside the mark. Without the mark, selecting
    // nothing is still a dead end and keeps today's warning (S58/E7).
    if (instanceSelections.length === 0 && !generateMissing) {
      showAlert({ severity: 'warning', title: t_('Select at least one instance') })
      return
    }
    const quantity = roundQty(instanceSelections.reduce((sum, item) => sum + item.quantity, 0))

    // The mark carries no amount: Sun derives what is missing for the whole report (DG-H1), so a
    // single press resolves the batch and the front can never ask for more than the pending.
    const generationMark = generateMissing ? { generateMissing: true } : {}

    // S16: selection-only mode — hand the picked instances back to the caller (the auto-consume
    // report flow), never posting the manual consumption endpoint. The caller owns the request, and
    // with it the in-flight guard.
    if (mode === 'select') {
      if (onSelect) onSelect(instanceSelections, quantity, generateMissing)
      return
    }

    // The consumption is attributed to the active part selected in the components panel (the batch
    // endpoint now groups materials by part). Without a selected part there is nothing to attribute
    // it to, so we stop and let the operator pick one first.
    if (!partId) {
      showAlert({ severity: 'warning', title: t_('Select a part first') })
      return
    }

    // S33: every control, the generation action included, stays disabled while the request is in
    // flight, so a double tap cannot produce two generations.
    setLoading(true)
    try {
      await apiPost('partWorkflowHistory/rawMaterial/byFoAndNodeInstance/' + deviceId + '/' + fabricationOrderId + '/' + nodeId, [
        {
          partId,
          materials: [
            {
              rawMaterialReferenceId: referenceId,
              quantity,
              instanceSelections,
              ...generationMark
            }
          ]
        }
      ])
      if (onSuccess) onSuccess()
    } catch (error) {
      showAlert({ severity: 'error', title: t_(error.message) })
    } finally {
      setLoading(false)
    }
  }, [quantityByInstanceId, deviceId, fabricationOrderId, nodeId, referenceId, partId, showAlert, t_, onSuccess, mode, onSelect, generateMissing])

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Select instances of {{reference}}', { reference: referenceCode }) })}
      setOpen={open}
      cancelCallback={onClose}
      acceptCallback={submitSelection}
      disableButtons={loading}
    >
      {AlertElement}
      <NumericKeypad
        open={keypadInfo.open}
        onClose={() => setKeypadInfo((prev) => ({ ...prev, open: false }))}
        onAccept={handleKeypadAccept}
        initialValue={keypadInfo.val}
        title={t_('Quantity to consume')}
        allowDecimals={keypadInfo.fractionable}
      />
      {(hasBudget || canCreateRequiredMaterial) && (
        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1} sx={{ mb: 1 }}>
          {hasBudget && (
            <Typography
              variant='body2'
              color={selectedTotal >= requiredQuantity ? 'success.main' : 'text.secondary'}
              sx={{ fontWeight: 700 }}
            >
              {t_('Selected {{selected}} of {{required}}', { selected: selectedTotal, required: requiredQuantity })}
            </Typography>
          )}
          {canCreateRequiredMaterial && (
            <Button
              data-testid='create-required-material'
              variant={generateMissing ? 'contained' : 'outlined'}
              size='small'
              disabled={loading}
              onClick={() => setGenerateMissing((marked) => !marked)}
            >
              {t_('Create required material')}
            </Button>
          )}
        </Stack>
      )}
      <MosSmartTable columns={columns} dataFetch={fetchInstances} pageable tablePageSize={10} showSearchHeader hideExport />
    </MosSmartDialog>
  )
}

export default InstanceSelectionDialog
